import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BsChevronDown, BsLockFill, BsPlus } from "react-icons/bs";
import { MdOutlineFormatListBulleted } from "react-icons/md";
import { IoSearch } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { Button, Checkbox } from "@nextui-org/react";

import MFloatingComponent from "../Kit/MFloatingComponent";

import { fetchLists } from "../../redux/actions/listsActions";
import { auth } from "../../conf/firebase";
import myAxios from "../../utils/axiosWrapper";
import getConfig from "../../conf/config";
import { normalizeForSearch } from "../../utils/stringUtils";

// Above this many lists, scanning becomes tedious: show the search field.
const SEARCH_THRESHOLD = 7;

const MAddToListsMenu = ({ isTopPositioned = false }) => {
  const [inputName, setInputName] = useState("");
  const [isNewListPrivate, setIsNewListPrivate] = useState(false);
  const [isFloatingMenuDisplayed, setIsFloatingMenuDisplayed] = useState(false);
  const [selectedListsIds, setSelectedListsIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { lists, loading } = useSelector((state) => state.lists.lists);
  const { selectedCalls = [] } = useSelector((state) => state.calls.calls);
  const { user } = useSelector((state) => state.user);
  const { currentUser } = auth;

  const activeLists = lists.filter((list) => !list.is_archived && !list.is_deleted);
  const isSearchDisplayed = activeLists.length >= SEARCH_THRESHOLD;
  const normalizedQuery = normalizeForSearch(searchQuery);
  const filteredLists =
    isSearchDisplayed && normalizedQuery
      ? activeLists.filter((list) => normalizeForSearch(list.name).includes(normalizedQuery))
      : activeLists;

  const hasSelectedLists = selectedListsIds.length > 0;
  const hasSelectedCalls = selectedCalls.length > 0;

  const dispatchFetchLists = () => {
    dispatch(fetchLists());
  };

  const onCreateList = async () => {
    if (inputName.length <= 0) {
      return;
    }

    const response = await myAxios.post(`${getConfig().API_URL}/audio/api/v1/lists`, {
      list_name: inputName,
      is_private: isNewListPrivate,
    });
    setInputName("");
    setIsNewListPrivate(false);
    dispatchFetchLists();
  };

  useEffect(() => {
    if (!currentUser || currentUser === null) {
      return;
    }

    dispatchFetchLists();
  }, [user]);

  // Reset the query on close, focus it on open.
  useEffect(() => {
    if (!isFloatingMenuDisplayed) {
      setSearchQuery("");
      return;
    }
    if (isSearchDisplayed) {
      searchInputRef.current?.focus();
    }
  }, [isFloatingMenuDisplayed, isSearchDisplayed]);

  const onRemoveSelection = (list) => () => {
    let newSelectedListIds = [...selectedListsIds];
    newSelectedListIds = newSelectedListIds.filter((listId) => listId !== list.id);
    setSelectedListsIds(newSelectedListIds);
  };

  const onAddSelection = (list) => () => {
    const newSelectedListIds = [...selectedListsIds];
    newSelectedListIds.push(list.id);
    setSelectedListsIds(newSelectedListIds);
  };

  const onAddCallsToLists = async () => {
    const response = await myAxios.put(
      `${getConfig().API_URL}/audio/api/v1/lists/add_calls_lists`,
      {
        calls_ids: selectedCalls.map((call) => call.id),
        lists_ids: selectedListsIds,
      }
    );

    setSelectedListsIds([]);
    setIsFloatingMenuDisplayed(false);
  };

  const renderTrigger = () => (
    <div
      className="cursor-pointer flex items-center bg-white rounded-md px-[12px] py-[8px] text-[#33403F] text-sm"
      onClick={() => {
        setIsFloatingMenuDisplayed(!isFloatingMenuDisplayed);
      }}
      onKeyDown={() => {
        setIsFloatingMenuDisplayed(!isFloatingMenuDisplayed);
      }}
    >
      <span className="font-medium mr-[8px] whitespace-nowrap">{t("Add to a list")}</span>
      <MdOutlineFormatListBulleted
        size={18}
        color="#737373"
      />
    </div>
  );

  const renderFLoatingMenu = () => (
    <div
      className={`mt-1.5 bg-white rounded-lg p-2 text-[#33403F] drop-shadow-sm ${
        isSearchDisplayed ? "min-w-[220px]" : ""
      }`}
    >
      {isSearchDisplayed && (
        <div className="relative mb-1.5">
          <IoSearch
            size={14}
            color="#737373"
            className="absolute left-[8px] top-1/2 -translate-y-1/2 pointer-events-none"
          />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            placeholder={t("Search")}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full text-sm rounded-md border-1 border-solid border-[#E5E5E5] bg-white py-1 pl-[28px] pr-[8px] outline-none focus:border-primary"
          />
        </div>
      )}
      <div className="mb-3 max-h-[260px] overflow-y-auto">
        {filteredLists.length === 0 ? (
          <div className="py-1.5 px-1 text-sm text-[#737373] whitespace-nowrap">
            {t("No result")}
          </div>
        ) : (
          filteredLists.map((activeList) => {
            const isListSelected = selectedListsIds.includes(activeList.id);

            return (
              <div
                key={activeList.id}
                className="flex items-center py-1.5"
              >
                <Checkbox
                  isSelected={isListSelected}
                  onValueChange={
                    isListSelected ? onRemoveSelection(activeList) : onAddSelection(activeList)
                  }
                />
                <span className="ml-1 text-sm">{activeList.name}</span>
                {activeList.is_private && (
                  <BsLockFill
                    size={11}
                    className="ml-1.5 shrink-0 text-[#737373]"
                    title={t("Liste privée : visible uniquement par vous")}
                  />
                )}
              </div>
            );
          })
        )}
      </div>
      <div className="flex items-center mb-2 text-sm">
        <BsPlus size={24} />
        <span className="whitespace-nowrap ml-1 mr-4">{t("Create new list")}</span>
        <BsChevronDown
          style={{ marginLeft: "auto" }}
          size={16}
        />
      </div>
      <div className="flex items-center rounded-md border-2 border-[#E5E5E5] border-solid bg-white pl-2 text-sm">
        <input
          className="outline-none	border-0"
          placeholder="Name"
          value={inputName}
          onChange={(e) => setInputName(e.target.value)}
        />
        <div
          onClick={onCreateList}
          onKeyDown={onCreateList}
          className="cursor-pointer rounded-md border-2 border-[#E5E5E5] border-solid px-3 py-2"
          style={{ margin: "-2px" }}
        >
          {t("Create")}
        </div>
      </div>
      <Checkbox
        size="sm"
        className="mt-1.5"
        isSelected={isNewListPrivate}
        onValueChange={setIsNewListPrivate}
      >
        <span className="flex items-center gap-1 text-sm text-[#33403F]">
          <BsLockFill size={11} />
          {t("Liste privée")}
        </span>
      </Checkbox>
    </div>
  );

  return (
    <div className="flex items-center">
      <MFloatingComponent
        isTopPositioned={isTopPositioned}
        targetElement={renderTrigger()}
        floatingElement={renderFLoatingMenu()}
        isFloatingDisplayed={isFloatingMenuDisplayed}
        setIsFloatingDisplayed={setIsFloatingMenuDisplayed}
      />
      {hasSelectedLists && hasSelectedCalls && (
        <Button
          className="ml-2 text-sm"
          size="sm"
          color="primary"
          radius="md"
          onPress={() => onAddCallsToLists()}
        >
          <div>{t("Add")}</div>
        </Button>
      )}
    </div>
  );
};

export default MAddToListsMenu;
